import { Component, HostListener, inject, input, output, signal } from '@angular/core';
import { ICanvasElement, ICanvasElementText } from '@features/canvas/canvas.types';
import { VisualElementComponent } from '@features/canvas/visual-element/visual-element.component';
import { DebugRectComponent } from '@features/debug/debug-rect/debug-rect.component';
import { LpTextComponent } from '@features/lp/lp-text/lp-text.component';
import { getFontSize } from '@features/lp/lp-text/lp-text.utils';
import { StyleSheetSpriteComponent } from '@features/styles/style-sheet-sprite/style-sheet-sprite.component';
import { StylesService } from '@features/styles/styles.service';
import { LpLabelPosition } from '@typings/lp/generated';

@Component({
  selector: 'app-canvas-element',
  imports: [VisualElementComponent, DebugRectComponent, StyleSheetSpriteComponent, LpTextComponent],
  templateUrl: './canvas-element.component.html',
  styleUrl: './canvas-element.component.css',
})
export class CanvasElementComponent {
  readonly _stylesService = inject(StylesService);

  el = input.required<ICanvasElement>();
  clicked = output();

  // DRAG
  canDrag = signal(false);
  isDragging = signal(false);
  grabbed = output<PointerEvent>();
  dragged = output<PointerEvent>();
  dropped = output<PointerEvent>();

  getStyle(text: ICanvasElementText) {
    return {
      ...this.getPositionStyle(text.position),
      fontSize: getFontSize(text.style),
    };
  }

  private getPositionStyle(position: LpLabelPosition) {
    switch (position) {
      case 'In':
        return { inset: 0, textAlign: 'center' };
      case 'Left':
        return { width: '200%', top: '0', right: 'calc(100% + 0.25em)', bottom: '0', textAlign: 'right', justifyItems: 'right' };
      case 'Above':
        return { right: '-50%', bottom: 'calc(100% + 0.25em)', left: '-50%', textAlign: 'center', alignItems: 'bottom' };
      case 'Right':
        return { width: '200%', top: '0', bottom: '0', left: 'calc(100% + 0.25em)', textAlign: 'left', justifyItems: 'left' };
      case 'Below':
        return {
          top: 'calc(100% + 0.25em)',
          right: '-50%',
          left: '-50%',
          textAlign: 'center',
          justifyItems: 'top',
        };
      default: {
        throw new Error(`Position '${position}' not supported`);
      }
    }
  }

  @HostListener('pointerdown', ['$event'])
  handlePointerDown(event: PointerEvent) {
    if (!this.el().draggable) return;

    this.canDrag.set(true);

    event.stopPropagation(); // NOTE: this is to prevent also dragging the canvas
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent) {
    if (!this.canDrag()) return;

    if (!this.isDragging()) {
      this.grabbed.emit(event);
      this.isDragging.set(true);
    }

    this.dragged.emit(event);
  }

  @HostListener('document:pointerup', ['$event'])
  handlePointerUp(event: PointerEvent) {
    if (this.isDragging()) {
      this.dropped.emit(event);
    }

    this.canDrag.set(false);
    this.isDragging.set(false);
  }
}
